import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ASSET_OFFICE_PLACEHOLDER_SRC } from '../constants/assetOfficePlaceholder';
import { useI18n } from '../i18n';
import styles from './AssetImageHeroCarousel.module.css';

const DRAG_CLICK_THRESHOLD_PX = 6;

type HeroSlideImageProps = {
  src: string;
  alt: string;
  enlargeable: boolean;
};

function HeroSlideImage({ src, alt, enlargeable }: HeroSlideImageProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const displaySrc = failed || !src.trim() ? ASSET_OFFICE_PLACEHOLDER_SRC : src;

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={[styles.image, enlargeable ? styles.imageEnlargeable : ''].filter(Boolean).join(' ')}
      data-hero-src={src}
      draggable={false}
      onError={() => {
        if (!failed) {
          setFailed(true);
        }
      }}
    />
  );
}

type AssetImageHeroCarouselProps = {
  images: string[];
  alt: string;
  onImageClick?: (src: string) => void;
  className?: string;
  /** Place dots over the image so parent overlays (distance) can sit on the photo. */
  overlayChrome?: boolean;
};

export function AssetImageHeroCarousel({
  images,
  alt,
  onImageClick,
  className,
  overlayChrome = false,
}: AssetImageHeroCarouselProps) {
  const { t } = useI18n();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  });
  const slides = images.length > 0 ? images : [ASSET_OFFICE_PLACEHOLDER_SRC];
  const placeholderOnly = images.length === 0;
  const canScroll = slides.length > 1;

  const syncActiveFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || slides.length <= 1) {
      setActiveIndex(0);
      return;
    }
    const width = el.clientWidth;
    if (width <= 0) {
      return;
    }
    const pos = el.scrollLeft;
    const idx = Math.round(pos / width);
    setActiveIndex(Math.min(Math.max(idx, 0), slides.length - 1));
  }, [slides.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => syncActiveFromScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncActiveFromScroll()) : null;
    ro?.observe(el);
    syncActiveFromScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [syncActiveFromScroll]);

  useEffect(() => {
    setActiveIndex(0);
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ left: 0, behavior: 'auto' });
    } else {
      el.scrollLeft = 0;
    }
  }, [slides.join('|')]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !canScroll) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) {
        return;
      }
      const next = el.scrollLeft + event.deltaY;
      if (next <= 0 || next >= maxScroll) {
        return;
      }
      event.preventDefault();
      el.scrollLeft = next;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [canScroll, slides.length]);

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const width = el.clientWidth;
    const left = index * width;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ left, behavior: 'smooth' });
    } else {
      el.scrollLeft = left;
    }
  }, []);

  const scrollByStep = useCallback(
    (direction: -1 | 1) => {
      const next = Math.min(Math.max(activeIndex + direction, 0), slides.length - 1);
      scrollToIndex(next);
    },
    [activeIndex, scrollToIndex, slides.length],
  );

  const onTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canScroll || event.button !== 0) {
      return;
    }
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    dragState.current = {
      active: true,
      startX: event.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    setIsDragging(true);
    el.setPointerCapture(event.pointerId);
  };

  const onTrackPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.active) {
      return;
    }
    const dx = event.clientX - dragState.current.startX;
    if (Math.abs(dx) > DRAG_CLICK_THRESHOLD_PX) {
      dragState.current.moved = true;
    }
    el.scrollLeft = dragState.current.startScroll - dx;
  };

  const endTrackDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.active) {
      return;
    }
    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    const moved = dragState.current.moved;
    dragState.current.active = false;
    setIsDragging(false);
    if (!moved && onImageClick && !placeholderOnly) {
      const slide = (event.target as HTMLElement).closest(`.${styles.slide}`);
      const src = slide?.querySelector('img')?.getAttribute('data-hero-src');
      if (src) {
        onImageClick(src);
      }
    }
  };

  return (
    <div
      className={[styles.root, overlayChrome ? styles.rootOverlayChrome : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        ref={scrollRef}
        className={[styles.track, isDragging ? styles.trackDragging : ''].filter(Boolean).join(' ')}
        aria-roledescription="carousel"
        onPointerDown={onTrackPointerDown}
        onPointerMove={onTrackPointerMove}
        onPointerUp={endTrackDrag}
        onPointerCancel={endTrackDrag}
      >
        {slides.map((src, i) => (
          <div key={`${src}-${i}`} className={styles.slide} data-carousel-item="true">
            <HeroSlideImage
              src={src}
              alt={i === 0 ? alt : ''}
              enlargeable={Boolean(onImageClick && !placeholderOnly)}
            />
          </div>
        ))}
      </div>
      {canScroll ? (
        <>
          <button
            type="button"
            className={[styles.navButton, styles.navPrev].join(' ')}
            aria-label={t('assetProfile.carouselPrev')}
            disabled={activeIndex <= 0}
            onClick={() => scrollByStep(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={[styles.navButton, styles.navNext].join(' ')}
            aria-label={t('assetProfile.carouselNext')}
            disabled={activeIndex >= slides.length - 1}
            onClick={() => scrollByStep(1)}
          >
            ›
          </button>
          <div className={styles.dots} role="tablist" aria-label={alt}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === activeIndex}
                className={[styles.dot, i === activeIndex ? styles.dotActive : ''].filter(Boolean).join(' ')}
                onClick={() => scrollToIndex(i)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
